import { TriangleIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useAutoTradingTimer } from "@/hooks/useAutoTradingTimer";
import { cn } from "@/lib/cn";
import { formatCurrency } from "@/lib/utils";

import { AutoTradingControlButton } from "@/components/AutoTradingControlButton";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/Icon";
import { PeriodPicker } from "@/components/ui/PeriodPicker";
import { Skeleton } from "@/components/ui/skeleton";
import { EARNINGS_PERIOD_PRESETS } from "@/constants/periods";
import { ROUTES } from "@/constants/routes";

export interface BalanceCardProps {
  capital: number;
  capitalChange: number;
  earnings: number;
  earningsPeriod: string;
  isLoading?: boolean;
  onChangePeriod?: (value: string) => void;
  fuelBalance: number;
  usedFuel: number;
}

export const BalanceCard = ({
  capital,
  capitalChange,
  earnings,
  earningsPeriod,
  isLoading,
  onChangePeriod,
  fuelBalance,
  usedFuel,
}: BalanceCardProps) => {
  const navigate = useNavigate();
  const timer = useAutoTradingTimer();
  const isPositiveChange = capitalChange > 0;
  const isPositiveEarnings = earnings >= 0;

  return (
    <div>
      <div className="flex w-full flex-col gap-4 rounded-md border border-neutral-200/7 bg-neutral-200/2 p-2 md:w-134">
        {/* Main Balance Section */}
        <div
          className={cn(
            "rounded-mds relative inset-0 flex min-h-55 flex-col justify-between overflow-hidden p-4",
            "bg-neutral-200/2 bg-[url('/images/decoration/balance-background.png')] bg-no-repeat",
            // mobile
            "bg-[size:180%] bg-[position:3%_40%]",
            // desktop
            "md:h-55 md:bg-contain md:bg-[position:right_bottom] md:bg-bottom-right"
          )}
        >
          {/* Capital Info */}
          <div className="relative z-10 flex flex-col gap-2">
            {/*<span className="font-pixel fs-300 text-neutral-50 uppercase">ваш капитал</span>*/}
            <div className="flex min-w-0 flex-nowrap items-start gap-1">
              {isLoading ? (
                <Skeleton className="h-12 w-40" />
              ) : (
                <span className="fs-800 flex min-w-0 items-center gap-1.5 font-bold text-neutral-50 max-[360px]:!text-[length:var(--text-600)]">
                  {formatCurrency(capital)}
                  <Icon
                    name="usdt"
                    className="h-11 w-11 shrink-0 max-[360px]:h-7 max-[360px]:w-7"
                  />
                </span>
              )}
              {!isLoading && capitalChange !== 0 && (
                <div className="flex shrink-0 items-center gap-1 max-[360px]:translate-x-1 max-[360px]:-translate-y-2">
                  <TriangleIcon
                    className={cn(
                      "size-2 fill-current",
                      isPositiveChange ? "text-primary-500" : "rotate-180 text-red-500"
                    )}
                  />
                  <span
                    className={cn(
                      "fs-300 font-medium tracking-[-0.03em]",
                      isPositiveChange ? "text-primary-500" : "text-red-500"
                    )}
                  >
                    {formatCurrency(Math.abs(capitalChange), 2)}%
                  </span>
                </div>
              )}
            </div>
          </div>
          {/* Earnings Info */}
          <div className="relative z-10 flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="fs-300 font-semibold text-neutral-200/50">Заработано за</span>
              <PeriodPicker
                options={EARNINGS_PERIOD_PRESETS}
                value={earningsPeriod}
                onChange={onChangePeriod ?? (() => {})}
                size="md"
              />
            </div>
            {isLoading ? (
              <Skeleton className="h-8 w-48" />
            ) : (
              <div className="flex min-w-0 flex-wrap items-center gap-3">
                <span
                  className={cn(
                    "fs-500 flex items-center gap-1 font-bold",
                    isPositiveEarnings ? "text-primary-500" : "text-red-500"
                  )}
                >
                  {isPositiveEarnings ? "+ " : "- "}
                  {formatCurrency(Math.abs(earnings))}
                  <Icon
                    name="usdt"
                    className="h-6 w-6 shrink-0"
                  />
                </span>

                {/* Вертикальный разделитель */}
                <div className="self-stretch border-l border-neutral-200/22 max-[360px]:hidden" />

                {/* Затраченное топливо */}
                <span className={cn("fs-500 flex items-center gap-1 font-bold")}>
                  - {formatCurrency(usedFuel)}
                  <Icon
                    name="fuel"
                    className="h-6 w-6 text-white"
                  />
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Bottom section: Timer and Action Button */}
        <div className="flex items-center justify-end gap-4">
          {timer && <span className="fs-200 font-medium text-neutral-50/50">{timer}</span>}
          <AutoTradingControlButton
            showText
            size="md"
          />
        </div>
      </div>
      <div className="mt-4 rounded-2xl bg-[linear-gradient(0deg,rgba(249,249,249,0.1),rgba(249,249,249,0.1)),linear-gradient(180deg,rgba(249,249,249,0.001)_0%,rgba(198,253,0,0.12)_100%)] p-px">
        <div className="rounded-2xl bg-neutral-950">
          <div
            className={cn(
              "flex w-full min-w-0 flex-col items-center gap-4 rounded-2xl p-2 md:flex-row md:justify-between md:gap-0",
              "bg-[linear-gradient(0deg,rgba(249,249,249,0.02),rgba(249,249,249,0.02)),linear-gradient(180deg,rgba(198,253,0,0.018),rgba(198,253,0,0.066))]",
              "backdrop-blur-[42px]"
            )}
          >
            <div className="flex w-full items-center justify-center gap-1 md:w-auto md:justify-start">
              <span className="fs-300 p-2 font-semibold text-neutral-200/50">Запас топлива</span>

              {isLoading ? (
                <Skeleton className="h-6 w-24" />
              ) : (
                <span className="fs-500 flex items-center gap-1 font-bold">
                  {formatCurrency(fuelBalance)}
                  <Icon
                    name="fuel"
                    className="h-4 w-4 text-white"
                  />
                </span>
              )}
            </div>
            <Button
              variant="secondary"
              size="default"
              className="w-full md:w-auto md:min-w-[171px] backdrop-blur-[42px] transition-colors"
              onClick={() => navigate(ROUTES.buyFuel)}
            >
              Купить топливо
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
